<template>
  <v-dialog
    v-model="isDialogOpen"
    @click:outside="onClose"
    @keydown.esc="onClose"
  >
    <div class="access-rights">
      <v-card>
        <v-card-title>
          <v-text-field
            append-icon="mdi-magnify"
            label="Поиск по названию"
            single-line
            hide-details
            @input="debouncedSearch"
          />
        </v-card-title>

        <v-data-table
          height="500"
          :headers="tableHeaders"
          :items="sortableList"
          show-select
          fixed-header
          item-key="id"
          class="access-rights__table access-rights pt-6"
          no-data-text="Разрешения для слоёв отсутствуют"
          hide-default-footer
          disable-pagination
          @current-items="refreshIntersectionObserver"
        >
          <template v-slot:[`header.data-table-select`]>
            <v-col
              v-if="!isOneItemSelected"
              class="d-flex justify-center align-center flex-column pa-0 text-center"
            >
              <v-checkbox
                class="ma-0"
                :value="isCheckedAllRows"
                @change="checkedAllRows($event)"
              />
              Множественный выбор
            </v-col>
          </template>

          <template
            v-for="column in [...actionPermissionsList, 'checkAll']"
            :slot="`header.${column}`"
            slot-scope="{ header }"
          >
            <v-col
              :key="`header-${column}`"
              class="d-flex justify-center align-center flex-column pa-0"
            >
              <v-checkbox
                v-if="!isOneItemSelected && selectedRows.length"
                class="ma-0"
                @change="checkColumn($event, header.value)"
              />
              {{ header.text }}
            </v-col>
          </template>

          <template v-slot:item="{ item }">
            <tr
              :id="`table-row-${item.id}`"
              :data-id="item.id"
              class="access-rights__table-row"
            >
              <td>
                <v-col
                  v-if="visibility[item.id]"
                  class="d-flex justify-center pa-0"
                >
                  <v-checkbox
                    v-if="!isOneItemSelected"
                    :key="item.id"
                    v-model="selectedRows"
                    :value="item.id"
                    class="ma-0"
                  />
                </v-col>
              </td>

              <td>
                <v-card-text class="pa-0">
                  {{ item.name }}
                </v-card-text>
              </td>

              <td
                v-for="permission in actionPermissionsList"
                :key="permission"
              >
                <v-col
                  v-if="visibility[item.id]"
                  class="d-flex justify-center pa-0"
                >
                  <v-checkbox
                    v-model="userLayersPermissions[item.id]"
                    class="ma-0 d-inline-flex"
                    :disabled="isCheckboxDisabled(item)"
                    :value="actionPermissions[permission]"
                  />
                </v-col>
              </td>

              <td>
                <v-col
                  v-if="visibility[item.id]"
                  class="d-flex justify-center pa-0"
                >
                  <v-checkbox
                    class="ma-0"
                    :disabled="isCheckboxDisabled(item)"
                    :input-value="userLayersPermissions[item.id] &&
                      userLayersPermissions[item.id].length === actionPermissionsLength"
                    @change="checkAll($event, item.id)"
                  />
                </v-col>
              </td>
            </tr>
          </template>
        </v-data-table>

        <v-card-actions>
          <div class="flex-grow-1" />

          <v-btn text color="success" @click="onSave">
            Сохранить
          </v-btn>

          <v-btn text color="blue darken-1" @click="onClose">
            Отменить
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </v-dialog>
</template>

<script>
/* eslint-disable no-restricted-syntax */

import { mapGetters, mapState, mapActions } from 'vuex';
import debounce from 'lodash/debounce';

const TRANSLATION = {
  'layer-data-delete': 'Удаление слоя',
  'layer-data-write': 'Изменение слоя',
  'layer-data-read': 'Чтение слоя',
  'geometry-delete': 'Удаление объектов слоя',
  'geometry-write': 'Изменение объектов слоя',
  'geometry-read': 'Чтение объектов слоя',
};

const LAYER_NAME_HEADER = {
  text: 'Название',
  value: 'layerId',
  sortable: false,
  align: 'start',
  width: '300',
};

const CHECK_ALL_HEADER = {
  text: 'Выбрать все',
  value: 'checkAll',
  sortable: false,
  align: 'center',
};

export default {
  name: 'user-layers-access-table',

  props: {
    isDialogOpen: {
      type: Boolean,
      default: false,
    },
    isOneItemSelected: {
      type: Boolean,
      default: false,
    },
    layers: {
      type: Array,
      default: () => ([]),
    },
    data: {
      type: [Object, Array],
      required: true,
    },
  },

  data: () => ({
    searchQuery: '',
    tableHeaders: [],
    selectedRows: [],
    actionPermissionsList: [],
    userLayersPermissions: {},
    accessRightsTable: null,
    rowList: null,
    visibility: {},
    intersectionObserver: null,
  }),

  computed: {
    ...mapState('layer-objects', ['actionPermissions']),
    ...mapGetters('layer-objects', ['objectLayers']),

    isCheckboxDisabled() {
      return (item) => {
        if (!this.isOneItemSelected) return false;
        const isEmpty = Object.keys(this.userLayersPermissions)
          .every((id) => !this.userLayersPermissions[id].length);
        if (isEmpty) return false;

        return this.userLayersPermissions[item.id].length === 0;
      };
    },

    layersItems() {
      // в this.layers может быть массив с id слоёв
      // или массив объектов { id: '', name: '' }

      if (this.layers.length && typeof this.layers[0] === 'object') {
        return [...this.layers];
      }

      return this.getLayersItems(this.layers);
    },

    filteredList() {
      let list = this.layersItems;

      if (this.searchQuery) {
        list = list.filter(({ name }) => name.toLowerCase()
          .indexOf(this.searchQuery?.toLowerCase()) !== -1);
      }

      return list;
    },

    sortableList() {
      const list = this.filteredList;

      return list.sort((a, b) => {
        const isItemALength = this.userLayersPermissions[a.id]?.length > 0;
        const isItemBLength = this.userLayersPermissions[b.id]?.length > 0;

        if (isItemALength && isItemBLength) return 0;
        if (isItemALength) return -1;
        return 1;
      });
    },

    actionPermissionsLength() {
      return this.actionPermissionsList.length;
    },

    isCheckedAllRows() {
      const selectedRows = this.selectedRows?.length;
      const allItems = this.sortableList?.length;

      return allItems && selectedRows && (allItems === selectedRows);
    },
  },

  async created() {
    this.debouncedSearch = debounce(this.onSearch, 500);

    if (!Object.keys(this.actionPermissions).length) await this.getActionPermissions();

    this.actionPermissionsList = this.getActionPermissionsList();
    this.tableHeaders = [LAYER_NAME_HEADER, ...this.getPermissionsHeaders(), CHECK_ALL_HEADER];

    for (const { id } of this.layersItems) {
      if (this.data[id]) {
        this.$set(this.userLayersPermissions, id, [...this.data[id]]);
      } else if (!this.userLayersPermissions[id]) {
        this.$set(this.userLayersPermissions, id, []);
      }
    }
  },

  beforeDestroy() {
    this.disconnectIntersectionObserver();

    this.searchQuery = '';
    this.tableHeaders = [];
    this.selectedRows = [];
    this.actionPermissionsList = [];
    this.userLayersPermissions = {};
    this.visibility = {};
  },

  methods: {
    ...mapActions('layer-objects', ['getActionPermissions']),

    getActionPermissionsList() {
      const actions = ['read', 'write', 'delete'];
      const defaultOrder = ['layer-data', 'geometry'];
      const keys = Object.keys(this.actionPermissions).filter((_) => _ !== '_debug');

      const groups = keys.map((item) => item.slice(0, item.lastIndexOf('-')));
      const allGroups = [...defaultOrder, ...groups.filter((item) => !defaultOrder.includes(item))];
      const groupList = Array.from(new Set(allGroups));

      const list = [];
      for (const group of groupList) {
        for (const action of actions) {
          list.push(`${group}-${action}`);
        }
      }

      return list;
    },

    getLayersItems(layersIds) {
      const items = [];

      for (const id of layersIds) {
        if (this.objectLayers[id]) {
          items.push(this.objectLayers[id]);
        }
      }

      return items;
    },

    checkColumn(value, columnId) {
      if (columnId === 'checkAll') {
        const permissionsValues = Object.values(this.actionPermissions);

        for (const rowId of this.selectedRows) {
          this.userLayersPermissions[rowId] = value ? [...permissionsValues] : [];
        }
      } else {
        const permissionValue = this.actionPermissions[columnId];

        for (const rowId of this.selectedRows) {
          const index = this.userLayersPermissions[rowId].findIndex((p) => p === permissionValue);

          if (value && index === -1) {
            this.userLayersPermissions[rowId].push(permissionValue);
          } else if (!value && index !== -1) {
            this.userLayersPermissions[rowId].splice(index, 1);
          }
        }
      }
    },

    onSearch(query) {
      this.searchQuery = query;
    },

    refreshIntersectionObserver() {
      this.disconnectIntersectionObserver();

      this.$nextTick(() => {
        this.connectIntersectionObserver();
      });
    },

    connectIntersectionObserver() {
      this.accessRightsTable = document.querySelector('.access-rights__table');
      this.rowList = document.querySelectorAll('.access-rights__table-row');

      function callback(entries) {
        for (const entry of entries) {
          const { id } = entry.target.dataset;

          if (entry.isIntersecting) {
            this.$set(this.visibility, id, entry.isIntersecting);
          } else if (this.visibility[id] && entry.intersectionRatio === 0) {
            this.$set(this.visibility, id, false);
          }
        }
      }

      this.intersectionObserver = new IntersectionObserver(callback.bind(this), {
        root: this.accessRightsTable,
        rootMargin: '0px 0px 75px 0px',
        delay: 100,
        threshold: 0.5,
        trackVisibility: true,
      });

      for (const row of Array.from(this.rowList)) {
        this.intersectionObserver.observe(row);
      }
    },

    disconnectIntersectionObserver() {
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
      this.accessRightsTable = null;
      this.rowList = null;
    },

    checkedAllRows(value) {
      if (value) {
        this.selectedRows = this.sortableList.map(({ id }) => id);
      } else {
        this.selectedRows = [];
      }
    },

    getPermissionsHeaders() {
      const headers = [];

      for (const permission of this.actionPermissionsList) {
        headers.push({
          text: TRANSLATION[permission] || permission,
          value: permission,
          sortable: false,
          align: 'center',
        });
      }

      return headers;
    },

    checkAll(value, layerId) {
      if (value) {
        this.userLayersPermissions[layerId] = [...Object.values(this.actionPermissions)];
      } else {
        this.userLayersPermissions[layerId] = [];
      }
    },

    onClose() {
      this.$emit('closed');
    },

    onSave() {
      const data = {};

      // eslint-disable-next-line
      for (const key in this.userLayersPermissions) {
        const permission = this.userLayersPermissions[key];

        if (permission.length) {
          data[key] = permission;
        }
      }

      this.$emit('change', data);
    },
  },
};
</script>

<style lang="scss">
.v-messages {
  display: contents;
}

.access-rights {
  width: fit-content;
  background: white;
  margin: 0 auto;
  padding: 24px;
  width: 100%;
}
</style>
